import { NavLink } from 'react-router-dom'
import { useAuth } from '@/lib/auth'

interface ItemMenu {
  nomor: number
  judul: string
  path: string
  izin: string[]
}

interface GrupMenu {
  judul: string
  item: ItemMenu[]
}

/**
 * 24 menu utama — URUTAN PERSIS sesuai spesifikasi. Setiap item difilter
 * berdasarkan permission (kode sama persis dengan backend), sehingga menu yang
 * tidak berhak disembunyikan. Ini hanya kenyamanan UI; backend tetap menolak.
 */
export const MENU_UTAMA: GrupMenu[] = [
  {
    judul: 'Operasional Keuangan',
    item: [
      { nomor: 1, judul: 'Dashboard', path: '/', izin: ['dashboard:read', 'dashboard:read_own_unit', 'dashboard:read_all'] },
      { nomor: 2, judul: 'Tagihan Santri/Siswa', path: '/tagihan', izin: ['invoice:read'] },
      { nomor: 3, judul: 'Pembayaran', path: '/pembayaran', izin: ['payment:read'] },
      { nomor: 4, judul: 'Tunggakan', path: '/tunggakan', izin: ['arrears:read'] },
      { nomor: 5, judul: 'Kas Masuk', path: '/kas-masuk', izin: ['transaction:read'] },
      { nomor: 6, judul: 'Kas Keluar', path: '/kas-keluar', izin: ['transaction:read'] },
      { nomor: 7, judul: 'Kas dan Bank', path: '/kas-bank', izin: ['cashaccount:read'] },
      { nomor: 8, judul: 'Anggaran', path: '/anggaran', izin: ['budget:read'] },
      { nomor: 9, judul: 'Kas Kecil', path: '/kas-kecil', izin: ['pettycash:read'] },
      { nomor: 10, judul: 'Transfer Antarunit', path: '/transfer-antarunit', izin: ['transfer:read'] },
      { nomor: 11, judul: 'Pertanggungjawaban', path: '/pertanggungjawaban', izin: ['accountability:read'] },
      { nomor: 12, judul: 'Persetujuan', path: '/persetujuan', izin: ['transaction:approve', 'budget:approve', 'pettycash:approve', 'accountability:approve', 'transfer:approve'] },
    ],
  },
  {
    judul: 'Akuntansi & Pelaporan',
    item: [
      { nomor: 13, judul: 'Jurnal', path: '/jurnal', izin: ['journal:read'] },
      { nomor: 14, judul: 'Buku Besar', path: '/buku-besar', izin: ['ledger:read'] },
      { nomor: 15, judul: 'Neraca Saldo', path: '/neraca-saldo', izin: ['trialbalance:read'] },
      { nomor: 16, judul: 'Neraca', path: '/neraca', izin: ['balancesheet:read'] },
      { nomor: 17, judul: 'Laporan Aktivitas', path: '/laporan-aktivitas', izin: ['activityreport:read'] },
      { nomor: 18, judul: 'Arus Kas', path: '/arus-kas', izin: ['cashflow:read'] },
      { nomor: 19, judul: 'Laporan Konsolidasi', path: '/laporan-konsolidasi', izin: ['consolidation:read'] },
      { nomor: 20, judul: 'Rekonsiliasi', path: '/rekonsiliasi', izin: ['reconciliation:read'] },
    ],
  },
  {
    judul: 'Tata Kelola',
    item: [
      { nomor: 21, judul: 'Audit', path: '/audit', izin: ['audit:read'] },
      { nomor: 22, judul: 'Master Data', path: '/master-data', izin: ['unit:read', 'account:read', 'student:read', 'feetype:read', 'cashaccount:read', 'setting:read', 'fund:manage'] },
      { nomor: 23, judul: 'Pengguna dan Hak Akses', path: '/pengguna-hak-akses', izin: ['user:read', 'role:read'] },
      { nomor: 24, judul: 'Pengaturan', path: '/pengaturan', izin: ['setting:read', 'organization:read'] },
    ],
  },
]

export const MENU_WEBSITE: GrupMenu = {
  judul: 'Website & Publikasi',
  item: [
    { nomor: 26, judul: 'Konten Website', path: '/konten', izin: ['content:read'] },
    { nomor: 27, judul: 'Media Website', path: '/media', izin: ['media:read'] },
    { nomor: 28, judul: 'Pendaftar PPDB', path: '/pendaftar-ppdb', izin: ['admission:read'] },
  ],
}

export const MENU_PORTAL_WALI: GrupMenu = {
  judul: 'Portal Wali',
  item: [{ nomor: 25, judul: 'Portal Wali Santri', path: '/portal', izin: ['guardianportal:read'] }],
}

export function Sidebar({
  terbuka,
  onTutup,
}: {
  terbuka: boolean
  onTutup: () => void
}) {
  const { punyaIzin, pengguna } = useAuth()

  const grup = [...MENU_UTAMA, MENU_WEBSITE, MENU_PORTAL_WALI]
    .map((g) => ({ ...g, item: g.item.filter((i) => punyaIzin(...i.izin)) }))
    .filter((g) => g.item.length > 0)

  return (
    <>
      {terbuka ? (
        <button
          type="button"
          aria-label="Tutup menu"
          className="fixed inset-0 z-30 bg-netral-900/40 lg:hidden"
          onClick={onTutup}
        />
      ) : null}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-72 flex-col border-r border-yayasan-900/30 bg-yayasan-900 text-yayasan-50 transition-transform lg:static lg:translate-x-0 ${
          terbuka ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center gap-3 border-b border-yayasan-800 px-4 py-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-yayasan-50 text-sm font-bold text-yayasan-900">
            YYS
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">Keuangan Yayasan</p>
            <p className="truncate text-xs text-yayasan-200">Sistem Informasi Terpadu</p>
          </div>
          <button
            type="button"
            onClick={onTutup}
            className="ml-auto rounded-md p-1 text-yayasan-100 hover:bg-yayasan-800 lg:hidden"
            aria-label="Tutup menu"
          >
            ✕
          </button>
        </div>

        <nav className="flex-1 space-y-4 overflow-y-auto px-2 py-3" aria-label="Menu utama">
          {grup.map((g) => (
            <div key={g.judul}>
              <p className="px-3 py-1 text-[11px] font-semibold tracking-wider text-yayasan-300 uppercase">
                {g.judul}
              </p>
              <ul className="mt-1 space-y-0.5">
                {g.item.map((i) => (
                  <li key={i.path}>
                    <NavLink
                      to={i.path}
                      end={i.path === '/'}
                      onClick={onTutup}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                          isActive
                            ? 'bg-yayasan-50 font-semibold text-yayasan-900'
                            : 'text-yayasan-100 hover:bg-yayasan-800'
                        }`
                      }
                    >
                      <span
                        className="w-5 shrink-0 text-right text-xs tabular-nums opacity-70"
                        aria-hidden="true"
                      >
                        {i.nomor}
                      </span>
                      <span className="truncate">{i.judul}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-t border-yayasan-800 px-4 py-3 text-xs text-yayasan-200">
          <p className="truncate font-medium text-yayasan-100">{pengguna?.fullName}</p>
          <p className="truncate">{pengguna?.roles.map((r) => r.name).join(', ') || 'Tanpa peran'}</p>
        </div>
      </aside>
    </>
  )
}
